import React, { useCallback, useEffect, useState } from 'react'
import { Box, Card, Grid } from '@mui/material'
import { CurrencySelect } from '../../currencies/components/CurrencySelect'
import { Currency } from '../../currencies/models/Currency'
import { DateDayControl } from 'src/admin/core/components/date/DateDayControl'
import { Agency, Enterprise, WorkstationProductsEnum } from 'src/core/models'
import { AgencySelect, UserAgenciesSelect, WorkstationProductSelect, ProviderSelect } from 'src/admin/core'
import { useLotsalcomFileDownloadUri } from '../hooks'
import { useAppTranslation } from 'src/admin/core/hooks'
import { saveAs } from 'file-saver'
import { DownloadButton, SearchButton } from 'src/admin/core'
import {
  dateIsBeforeThanTodayMoment,
  formatJSDateToLocal,
  isFutureDate,
  isSameDate,
} from 'src/admin/core/services/dateFunctions'

import { getAppBarState } from 'src/store'
import { UserEnterpriseSelect } from 'src/admin/core/components/Select/UserEnterprisesSelect'

export interface FilterModel {
  product: string
  locationId: string | undefined
  currencyId: string
  startDate: string
  endDate: string
  agencies: Agency[]
  provider?: string
  enterprise?: Enterprise
}

export const today = new Date()

const { userEnterprise } = getAppBarState().appState

export const initialFilters = {
  product: 'HORSE_RACES',
  currencyId: 'DOP',
  locationId: undefined,
  startDate: formatJSDateToLocal(today),
  endDate: formatJSDateToLocal(today),
  agencies: [],
  provider: '',
  enterprise: undefined,
}

export const Filters: React.FC<{ onChange: (filters: FilterModel) => void; onSearch: (filters: FilterModel) => void; isLoading?: boolean }> = ({ onChange, onSearch, isLoading = false }) => {
  const [filters, setFilters] = useState<FilterModel>(initialFilters)
  const { getLotsalcomFileDownloadUri, file } = useLotsalcomFileDownloadUri()
  const [readyToDownload, setReadyToDownload] = useState(true)
  const [isDefaultEnterprise, setIsDefaultEnterprise] = useState(false)

  const { label } = useAppTranslation()
  const startDate = filters.startDate

  // No initial filtering - all filtering is done through search button

  useEffect(() => {
    if (file && startDate && readyToDownload) {
      saveAs(file.sasUri, file.fileName)
    }
  }, [file, startDate, readyToDownload])

  const handleStartDateChange = (date: Date) => {
    setReadyToDownload(false)
    setFilters({
      ...filters,
      startDate: formatJSDateToLocal(date),
    })
  }

  const handleEndDateChange = (date: Date) => {
    setReadyToDownload(false)
    setFilters({
      ...filters,
      endDate: formatJSDateToLocal(date),
    })
  }

  const handleDownlaod = useCallback((date: string) => {
    getLotsalcomFileDownloadUri(date)
    setReadyToDownload(true)
  }, [])

  const handleSearch = useCallback(() => {
    // Send filters to parent and trigger search
    onChange(filters)
    onSearch(filters)
  }, [filters, onChange, onSearch])

  return (
    <Card
      sx={{
        p: 1,
      }}
    >
      <Box p={1}>
        <Grid container spacing={1}>
          <Grid item xs={12} md={isDefaultEnterprise ? 3 : 2} lg={isDefaultEnterprise ? 3 : 2} textAlign="left">
            <DateDayControl onChange={handleStartDateChange} showButtons={false} label={label('FECHA_DESDE')} />
          </Grid>
          <Grid item xs={12} md={isDefaultEnterprise ? 3 : 2} lg={isDefaultEnterprise ? 3 : 2} textAlign="left">
            <DateDayControl onChange={handleEndDateChange} showButtons={false} label={label('FECHA_HASTA')} />
          </Grid>
          <Grid item xs={12} md={2} lg={2}>
            <WorkstationProductSelect
              disableClearable={false}
              selectDefaultProduct={false}
              value={filters.product as WorkstationProductsEnum}
              onChange={(val) => {
                setFilters({
                  ...filters,
                  product: val || 'HORSE_RACES',
                });
              }}
              getDefaultValue={(val) => {
                setFilters({
                  ...filters,
                  product: val || 'HORSE_RACES',
                });
              }}
            />
          </Grid>
          {!isDefaultEnterprise && (
            <Grid item xs={12} md={2} lg={2}>
              <UserEnterpriseSelect selectDefaultEnterprise={false} onChangeValue={(value: Enterprise, action: string) => {
                  if (action === 'default') {
                    setIsDefaultEnterprise(true);
                    setFilters({
                      ...filters,
                      enterprise: value,
                      provider: '', // Set provider to empty string (TODOS) when enterprise is default
                    });
                  } else {
                    setIsDefaultEnterprise(false);
                    setFilters({
                      ...filters,
                      enterprise: value,
                    });
                  }
                }}
              />
            </Grid>
          )}
          <Grid item xs={12} md={2} lg={2}>
            <AgencySelect
              selectedEnterprise={filters.enterprise}
              disableClearable={false}
              onChangeValue={(value: Agency) =>
                setFilters({
                  ...filters,
                  locationId: value?.id,
                })
              }
            />
          </Grid>
          <Grid item xs={12} md={2} lg={2}>
            <ProviderSelect
              value={filters.provider}
              onChange={(value) =>
                setFilters({
                  ...filters,
                  provider: value || '',
                })
              }
              disableClearable={false}
            />
          </Grid>
          <Grid item xs={12} md={2} lg={2}>
            <SearchButton 
              color="primary" 
              onClick={handleSearch} 
              isLoading={isLoading}
              style={{ height: '56px', width: '100%' }}
            />
          </Grid>
        </Grid>
      </Box>
    </Card>
  )
}
